/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/bookings/:id`
 * API Version: 3.0
 * 
 * @param {string} bookingId - ID booking yang ingin diambil
 * @returns {object} - Data booking yang telah diformat
 * @example
 * const { data, isLoading, error } = useShowBooking("123");
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import ShowBookingResponseSchema from "../response/ShowBookingResponse";

const API_VERSION = "v3";

interface useShowBookingProps {
    bookingId: string;
    enabled?: boolean;
}

const useShowBooking = ({ bookingId, enabled = true }: useShowBookingProps) => {
    return useQuery({
        queryKey: ['bookings', 'booking', 'calendar', 'details', bookingId],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/booking/${bookingId}`, {
                    params: {
                        include: "property,roomType,room,ratePlan"
                    }
                });

                // Validasi response
                const validationResult = ShowBookingResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Booking data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Booking`, error);
                throw error;
            }
        },
        enabled: enabled && !!bookingId,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useShowBooking;